export type QueryStateType = "unknown" | "complete" | "error";

export function shouldUseMessageReconnectSnapshot(params: {
  channelId: string | null;
  queryState: QueryStateType;
  liveMessageCount: number;
  snapshotCount: number;
}): boolean {
  const { channelId, queryState, liveMessageCount, snapshotCount } = params;
  return (
    !!channelId &&
    queryState === "unknown" &&
    liveMessageCount === 0 &&
    snapshotCount > 0
  );
}

export function shouldShowMessageLoadingSkeleton(params: {
  channelId: string | null;
  queryState: QueryStateType;
  messageCount: number;
  expectedMinCount: number;
  hasCompletedOnce: boolean;
  /** When true, skip the skeleton — caller has determined the channel is empty. */
  channelKnownEmpty?: boolean;
  /** When true, partial local rows are safe to render immediately. */
  allowPartialLocalData?: boolean;
}): boolean {
  const {
    channelId,
    queryState,
    messageCount,
    expectedMinCount,
    hasCompletedOnce,
    channelKnownEmpty,
    allowPartialLocalData,
  } = params;
  // Skeleton fires while the local view hasn't reached the expected
  // page size AND we haven't seen complete yet. This protects against
  // two scenarios:
  //  1. Cold channel (non-preloaded): IndexedDB might have a few stray
  //     rows from other queries touching the messages table (e.g.
  //     inbox `useQuery` 5-min TTL); without a threshold we'd show
  //     them scattered, then jump to the full server-confirmed set.
  //  2. Preloaded channel: the workspace preload fills local data
  //     up to `PRELOAD_MESSAGES_PER_CHANNEL` per channel, and the
  //     caller passes that same value as `expectedMinCount`, so on
  //     first render the threshold is met instantly → no skeleton
  //     flash, contiguous most-recent rows render, then the active
  //     query's deeper pages fade in.
  //
  // Once any complete has been seen (hasCompletedOnce=true), skeleton
  // stays hidden — lets transient WebSocket reconnects fall through
  // to the reconnect-snapshot path instead.
  //
  if (channelKnownEmpty) return false;
  if (allowPartialLocalData && messageCount > 0) return false;
  return (
    !!channelId &&
    queryState === "unknown" &&
    messageCount < expectedMinCount &&
    !hasCompletedOnce
  );
}

export function shouldShowEmptyMessageState(params: {
  queryState: QueryStateType;
  isLoadingSkeletonVisible: boolean;
  messageCount: number;
}): boolean {
  const { queryState, isLoadingSkeletonVisible, messageCount } = params;
  return (
    queryState === "complete" && !isLoadingSkeletonVisible && messageCount === 0
  );
}
